import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { en } from "@/locales/en";
import { ptPT } from "@/locales/pt-PT";
import { LANGUAGE_STORAGE_KEY, pickLanguage } from "@/lib/languageDetection";

export const SUPPORTED_LANGUAGES = ["en", "pt-PT"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = "en";

// Each language in its own name, so a reader can always find theirs. Not translated.
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: "English",
  "pt-PT": "Português",
};

export const resources = {
  en,
  "pt-PT": ptPT,
} as const;

export function isLanguage(value: unknown): value is Language {
  return SUPPORTED_LANGUAGES.includes(value as Language);
}

/**
 * Starting language: the visitor's saved choice, else the browser language
 * (any "pt*" gives pt-PT), else English (see pickLanguage). Outside a browser
 * (prerender) it is English. For logged in users, the language saved in
 * user_settings replaces this once settings load (see useSyncLanguageFromSettings).
 */
export function detectInitialLanguage(): Language {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // Storage blocked: fall back to the browser language.
  }
  return pickLanguage(
    stored,
    window.navigator.languages ?? [window.navigator.language],
  );
}

/** Switches the UI language and remembers it on this device. */
export function setLanguage(language: Language): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Storage blocked: the change still applies to this session.
  }
  if (i18n.language !== language) void i18n.changeLanguage(language);
}

/** The active UI language (falls back to English for anything unsupported). */
export function getCurrentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
}

function syncHtmlLang(language: string): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = language;
  }
}

i18n.on("languageChanged", syncHtmlLang);

// Resources are bundled, so init is synchronous (initAsync: false). That keeps the
// first render, the prerender build and tests free of a loading state.
i18n.use(initReactI18next).init({
  resources,
  lng: detectInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  defaultNS: "common",
  ns: Object.keys(en),
  // React already escapes rendered values.
  interpolation: { escapeValue: false },
  initAsync: false,
  react: { useSuspense: false },
});

syncHtmlLang(i18n.language);

export default i18n;
